import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import produce from 'immer'
import {
  Button,
  Form as AntForm,
  Col,
  Typography,
  Space,
  notification,
  Tooltip
} from 'antd'
import { useForm, FormProvider } from 'react-hook-form'
import { Link } from 'react-router-dom'

import { BodyContentColumn } from 'admin/lib/Layouts'
import { SectionProvider, Section, FormItem, Select } from 'admin/lib/Forms'

import * as ROUTES from 'admin/routes'
import { Queries } from 'admin/lib/Api'
import { useGlobalShortcut } from 'admin/lib/shortcuts'
import { Matchers } from 'admin/lib/Records'
import { ScreenSpin } from 'admin/lib/ScreenSpin'
import { ClipboardCopy } from 'admin/lib/ClipboardCopy'
import { Switch } from 'admin/lib/Switch'
import { AccountSelect } from 'admin/lib/EntityInputs'
import { handleApiFailure } from 'admin/lib/Common/apiFailureHandler'

export function EditPublicApiKeyForm({ apiKeyId, isNew }) {
  const registerer = Queries.useApiKeyRegister()
  const updater = Queries.useApiKeyUpdater()
  const upsert = isNew ? registerer : updater

  //
  // Form setup & values

  const form = useForm({
    defaultValues: {
      unhashedKey: null,
      hashedKey: null,
      name: '',
      isEnabled: true,
      isWebhookKey: false,
      accountIds: [],
      permissions: []
    },
    mode: 'all',
    criteriaMode: 'firstError'
  })

  const apiKey = Queries.useApiKey(isNew ? null : apiKeyId)
  const { reset: _reset, getValues } = form
  const reset = useCallback(
    data => {
      if (!data) {
        console.warn('Reset called with no data in api keys form')
        return
      }

      _reset({
        unhashedKey: data.unhashedKey,
        hashedKey: data.hashedKey,
        name: data.name,
        isEnabled: data.isEnabled,
        isWebhookKey: data.isWebhookKey,
        accountIds: data.apiKeyMemberships?.map(m => m.accountId) ?? [],
        permissions: data.permissions
      })
    },
    [_reset]
  )

  useEffect(() => {
    if (apiKey.data && !getValues().hashedKey) {
      reset(apiKey.data)
    }
  }, [apiKey.data, getValues, reset])

  const unhashedKey = form.watch('unhashedKey', apiKey.data?.unhashedKey)
  const isWebhookKey = form.watch('isWebhookKey', apiKey.data?.isWebhookKey)
  const accountIds = form.watch('accountIds', apiKey.data?.accountIds)

  //
  // Callbacks

  const onSubmit = useCallback(
    async data => {
      if (unhashedKey) {
        return
      }

      const apiCredentials = produce(apiKey.data ?? {}, draft => {
        draft.name = data.name
        draft.isEnabled = data.isEnabled
        draft.isWebhookKey = data.isWebhookKey
        draft.permissions = data.permissions
        draft.apiKeyMemberships = data.accountIds.map(accountId => ({
          accountId
        }))
      })

      try {
        const data = await upsert.mutateAsync({
          data: apiCredentials
        })

        reset(data)

        notification.success({
          message: 'API Key Saved Successfully 👍'
        })         
      } catch (error) {
        handleApiFailure(error)
      }
    },
    [apiKey.data, reset, unhashedKey, upsert]
  )
  const handleSubmit = form.handleSubmit(onSubmit)

  useGlobalShortcut(Matchers.form.save, () => handleSubmit())

  const isWebhookValid = useMemo(() => {
    return !isWebhookKey || accountIds.length === 1
  }, [accountIds, isWebhookKey])

  const webhookKeyLabelWithTooltip = useMemo(() => {
    if (isNew)
      return (<Tooltip title="For incoming messages from integrations like Procore *only*. Settable at creation only.">Webhook Key</Tooltip>)
    else 
      return (<Tooltip title="You cannot change an API Key to/from a Webhook key. Create a new API Key instead.">Webhook Key</Tooltip>)
  }, [isNew])

  const loading = !apiKey.isFetched

  if (loading) {
    return <ScreenSpin />
  }

  if (apiKey.isError) {
    return (
      <BodyContentColumn>
        <Typography.Text type="danger">
          {JSON.stringify(apiKey.error?.response?.data ?? apiKey.error)}
        </Typography.Text>
      </BodyContentColumn>
    )
  }

  return (
    <SectionProvider>
      <FormProvider {...form}>
        <BodyContentColumn>
          <AntForm layout="vertical" onFinish={handleSubmit}>
            {!unhashedKey && (
              <Section>
                {({ firstInputRef }) => (
                  <>
                    <Typography.Title level={4}>
                      {isNew ? 'Create API Key' : 'Update API Key'}
                    </Typography.Title>

                    <FormItem
                      name="name"
                      label="Name"
                      rules={{
                        minLength: 2,
                        required: true
                      }}
                      inputRef={firstInputRef}
                    />

                    <FormItem name="accountIds" label="Account Memberships" disabled={!isNew && isWebhookKey} >
                      <AccountSelect disabled={!isNew && isWebhookKey}/>
                    </FormItem>

                    {!isWebhookValid && (
                      <p className="validation-error">
                        Webhook Keys must be assigned to exactly one account.
                      </p>
                    )}

                    <FormItem label="Enabled" name="isEnabled">
                      <Switch />
                    </FormItem>

                    <FormItem label={webhookKeyLabelWithTooltip} name="isWebhookKey" disabled={!isNew}>
                      <Switch />
                    </FormItem>

                    <FormItem label="Permissions" name="permissions">
                      <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        options={[
                          { label: 'Read', value: 'Read' },
                          { label: 'Write', value: 'Write' }
                        ]}
                      />
                    </FormItem>
                  </>
                )}
              </Section>
            )}

            <Col align="middle">
              <Space direction="vertical">
                {unhashedKey && (
                  <Col align="middle">
                    <Typography.Text>
                      {'💪'} API Key created! Please store this key for later,
                      this is your{' '}
                      <Typography.Text type="danger" strong>
                        only chance
                      </Typography.Text>{' '}
                      to save it
                    </Typography.Text>

                    <ClipboardCopy value={unhashedKey} />
                  </Col>
                )}

                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    disabled={
                      upsert.isLoading ||
                      !form.formState.isValid ||
                      !form.formState.isDirty ||
                      unhashedKey ||
                      !isWebhookValid
                    }
                    style={{ width: '8rem' }}
                  >
                    {isNew ? 'Create' : 'Save'}
                  </Button>

                  <Link to={ROUTES.PUBLIC_API_KEYS.LIST}>
                    <Button
                      disabled={upsert.isLoading}
                      style={{ width: '8rem' }}
                    >
                      Back to list
                    </Button>
                  </Link>
                </Space>
              </Space>
            </Col>
          </AntForm>
        </BodyContentColumn>
      </FormProvider>
    </SectionProvider>
  )
}

EditPublicApiKeyForm.propTypes = {
  apiKeyId: PropTypes.string,
  isNew: PropTypes.bool.isRequired
}
